import {
    IPolicyType,
    IPolicyTypeUpdate,
    updatePolicyType,
} from "@/api/policy-types";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Edit } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface IProps {
    policyType: IPolicyType;
}

interface IPolicyTypeInfoForm {
    name: string;
    short_name: string;
}

/**
 * Section displaying policy type information with inline editing
 */
export const PolicyTypeInfoSection = ({ policyType }: IProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: updatePolicyTypeMutation, isPending: isSaving } =
        useMutation({
            mutationFn: async (formData: IPolicyTypeInfoForm) => {
                const policyTypeUpdate: IPolicyTypeUpdate = {
                    name: formData.name.trim(),
                    short_name: formData.short_name.trim(),
                };
                return updatePolicyType(policyType.id, policyTypeUpdate);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        "admin-policy-type-details",
                        String(policyType.id),
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: ["admin-policy-types"],
                });
                setIsEditing(false);
            },
        });

    const handleSave = (formData: IPolicyTypeInfoForm) => {
        updatePolicyTypeMutation(formData);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const defaultValues: IPolicyTypeInfoForm = {
        name: policyType.name,
        short_name: policyType.short_name,
    };

    return (
        <Panel
            title="Policy Type Information"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                        visibility: isEditing ? "hidden" : "visible",
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            {isEditing ? (
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSave}
                >
                    <Stack spacing={2.5}>
                        <FormTextFieldElement
                            label="Name"
                            name="name"
                            size="small"
                            rules={{
                                validate: (value: string) =>
                                    value.trim() !== "" ||
                                    "Name cannot be empty",
                            }}
                            fullWidth
                            required
                        />

                        <FormTextFieldElement
                            label="Short Name"
                            name="short_name"
                            size="small"
                            rules={{
                                validate: (value: string) =>
                                    value.trim() !== "" ||
                                    "Short Name cannot be empty",
                            }}
                            fullWidth
                            required
                        />

                        <Stack
                            direction="row"
                            gap={1}
                            justifyContent="flex-end"
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="small"
                                loading={isSaving}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </FormContainer>
            ) : (
                <Stack spacing={2}>
                    <InfoField label="Name" value={policyType.name} />
                    <InfoField
                        label="Short Name"
                        value={policyType.short_name}
                    />
                </Stack>
            )}
        </Panel>
    );
};

interface IInfoFieldProps {
    label: string;
    value: string;
}

const InfoField = ({ label, value }: IInfoFieldProps) => (
    <Stack spacing={0.5}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
            {label}
        </Typography>
        <Typography variant="body2">{value || "-"}</Typography>
    </Stack>
);
